import React, { createContext, useContext, useMemo } from 'react';
import {
    DEFAULT_REPORTING_HOST,
    buildViewerRequestOptions,
    buildDesignerRequestOptions,
} from '../config/defaults.js';
import {
    normalizeReportingLocale,
    reportingMessages,
} from '../localization/reporting-messages.js';

/**
 * @typedef {Object} ReportingContextValue
 * @property {string} host - Reporting server base URL
 * @property {string|undefined} authToken - JWT auth token (opsiyonel)
 * @property {string|undefined} tenantId - Doğrulanmış tenant bağlamı (opsiyonel)
 * @property {'tr'|'en'} locale - Rapor ve DevExpress arayüz dili
 * @property {(key: string) => string} translate - Paket içi raporlama metni çeviricisi
 * @property {Object} viewerRequestOptions - DX viewer için hazır requestOptions
 * @property {Object} designerRequestOptions - DX designer için hazır requestOptions
 */

const ReportingContext = createContext(null);

/**
 * ReportingProvider
 *
 * Uygulama kök bileşenine sarılır. Tüm raporlama config'ini context'e aktarır.
 * Alt bileşenler `useReporting()` hook'unu kullanarak config'e erişir.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.host] - Reporting server URL (varsayılan: http://localhost:5150)
 * @param {string} [props.authToken] - JWT token
 * @param {string} [props.tenantId] - Tenant kimliği
 * @param {'tr'|'en'|string} [props.locale] - Aktif uygulama dili
 * @param {Object} [props.messages] - Paket metinleri için locale bazlı override
 */
export function ReportingProvider({ children, host, authToken, tenantId, locale, messages }) {
    const parent = useContext(ReportingContext);
    const effectiveHost = host ?? parent?.host ?? DEFAULT_REPORTING_HOST;
    const effectiveAuthToken = authToken ?? parent?.authToken;
    const effectiveTenantId = tenantId ?? parent?.tenantId;
    const effectiveLocale = normalizeReportingLocale(locale ?? parent?.locale);
    const effectiveMessages = messages ?? parent?.messages;

    const value = useMemo(() => {
        const localizedMessages = reportingMessages(effectiveLocale, effectiveMessages);
        return {
            host: effectiveHost,
            authToken: effectiveAuthToken,
            tenantId: effectiveTenantId,
            locale: effectiveLocale,
            messages: effectiveMessages,
            translate: (key) => localizedMessages[key] ?? key,
            viewerRequestOptions: buildViewerRequestOptions(
                effectiveHost,
                effectiveAuthToken,
                effectiveTenantId,
                effectiveLocale,
            ),
            designerRequestOptions: buildDesignerRequestOptions(
                effectiveHost,
                effectiveAuthToken,
                effectiveTenantId,
                effectiveLocale,
            ),
        };
    }, [effectiveHost, effectiveAuthToken, effectiveTenantId, effectiveLocale, effectiveMessages]);

    return React.createElement(ReportingContext.Provider, { value }, children);
}

/**
 * useReporting hook
 * ReportingProvider olmadan kullanılırsa hata fırlatır.
 * @returns {ReportingContextValue}
 */
export function useReporting() {
    const ctx = useContext(ReportingContext);
    if (!ctx) {
        throw new Error('[useReporting] missing ReportingProvider');
    }
    return ctx;
}
